import MiniSearch, { type Options } from 'minisearch';

export interface SearchDoc {
  id: string;
  kind: 'program' | 'post';
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  image?: string;
  date?: string;
}

/**
 * Fuzzy, ranked search over programs + blog posts. The index is built at
 * build time (scripts/build-search-index.mjs) and loaded on demand — the
 * full post bodies (2.2MB) never reach the browser.
 */
export const SEARCH_OPTIONS: Options<SearchDoc> = {
  fields: ['title', 'excerpt', 'body'],
  storeFields: ['kind', 'slug', 'title', 'excerpt', 'image', 'date'],
  searchOptions: {
    boost: { title: 4, excerpt: 2 },
    fuzzy: 0.2,
    prefix: true,
  },
};

/**
 * Matching for the card grids (category pages, /program-filter), which have
 * only the fields a card carries. It reuses SEARCH_OPTIONS.searchOptions
 * verbatim so a grid ranks and fuzzy-matches exactly like the site search —
 * "build bikes" has to reach "Bike Build Donation®" in both places.
 */
export interface CardDoc {
  id: string;
  title: string;
  excerpt: string;
}

export const CARD_SEARCH_OPTIONS: Options<CardDoc> = {
  fields: ['title', 'excerpt'],
  searchOptions: SEARCH_OPTIONS.searchOptions,
};

/**
 * A fallback hit must score at least this fraction of the best hit to show.
 * Tuned on the CSR category: "donate bicycles to kids" goes from 16 of 26
 * cards to 4, while the genuinely-related ones survive.
 */
const RELEVANCE_FLOOR = 0.35;

/**
 * Ranked ids for a card-grid query, or undefined when the box is empty.
 *
 * MiniSearch combines terms with OR, so "build guitars" matched every card
 * containing "build" — 16 of 26, which barely filters. A filter box is
 * expected to narrow, so every term is required first; only when that finds
 * nothing (an unknown or extra word, e.g. "donate bicycles to kids") does it
 * fall back to OR, and then only hits close to the best one, so the long
 * weak tail stays off.
 */
export function rankCards(
  engine: MiniSearch<CardDoc>,
  query: string,
): Map<string, number> | undefined {
  const q = query.trim();
  if (!q) return undefined;
  const strict = engine.search(q, { combineWith: 'AND' });
  let hits = strict;
  if (hits.length === 0) {
    const loose = engine.search(q);
    const best = loose[0]?.score ?? 0;
    hits = loose.filter((hit) => hit.score >= best * RELEVANCE_FLOOR);
  }
  return new Map(hits.map((hit, index) => [String(hit.id), index]));
}

let indexPromise: Promise<MiniSearch<SearchDoc>> | undefined;

/** Loads (once) and returns the ready-to-query index. */
export async function loadSearchIndex(): Promise<MiniSearch<SearchDoc>> {
  indexPromise ??= import('@/data/search-index.json').then((module) => {
    const docs = (module.default ?? module) as unknown as SearchDoc[];
    const engine = new MiniSearch<SearchDoc>(SEARCH_OPTIONS);
    engine.addAll(docs);
    return engine;
  });
  return indexPromise;
}

export interface SearchHit {
  id: string;
  kind: 'program' | 'post';
  slug: string;
  title: string;
  excerpt: string;
  image?: string;
  score: number;
}

export function hrefFor(hit: Pick<SearchHit, 'kind' | 'slug'>): string {
  return hit.kind === 'program' ? `/programs/${hit.slug}` : `/${hit.slug}`;
}
